'use client';

import { c as _c } from "react/compiler-runtime";
import { reactive, useObservable, useObserveEffect } from '@legendapp/state/react';
import { $React } from '@legendapp/state/react-web';
import { type ReactNode } from 'react';
import { batch } from '@legendapp/state';
import { addDays, addMonths, eachDayOfInterval, endOfMonth, format, isAfter, isSameDay, startOfMonth } from 'date-fns';
import { toZonedTime } from 'date-fns-tz';
import { FieldWarning } from '@/components/FieldWarning';
import { useHeroBlockSearchFormState } from '@/components/forms/HeroBlockSearchForm/HeroBlockSearchFormProvider';
import { HeroSearchFormColumnButton, type HeroSearchFormColumnButtonProps } from '@/components/forms/HeroBlockSearchForm/components/HeroSearchFormColumnButton';
import { useSearchFromContext } from '@/components/forms/SearchFromProvider';
import { DateRangePicker } from '@/components/forms/fields/DateRangePicker';
import { cn } from '@/lib/cn';
import { isValidDate } from '@/lib/dates';
import { getBranchHolidaysForDate } from '@/lib/siteHolidays';
export type HeroSearchFormDateRangeSelectProps = HeroSearchFormColumnButtonProps;
const ReactiveDateRangePicker = reactive(DateRangePicker);
const ReactiveHeroSearchFormColumnButton = reactive(HeroSearchFormColumnButton);
function validateDateRange(from?: Date, to?: Date) {
  return isValidDate(from) && isValidDate(to) && !isSameDay(from, to);
}
export function HeroSearchFormDateRangeSelect(t0) {
  const $ = _c(100);
  let className;
  let description;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      description,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = description;
    $[3] = props;
  } else {
    className = $[1];
    description = $[2];
    props = $[3];
  }
  const {
    triggerId$,
    open$,
    values,
    variant$
  } = useHeroBlockSearchFormState();
  const formState$ = useSearchFromContext();
  const isNavigating$ = useObservable(false);
  const selecting$ = useObservable("pickUp");
  let t1;
  if ($[4] !== values.dropOffDate || $[5] !== values.pickUpDate) {
    t1 = () => {
      const from = values.pickUpDate.get();
      const to = values.dropOffDate.get();
      return validateDateRange(from, to);
    };
    $[4] = values.dropOffDate;
    $[5] = values.pickUpDate;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  const valid$ = useObservable(t1);
  let t2;
  if ($[7] !== formState$.dropOffDate || $[8] !== formState$.pickUpDate || $[9] !== values.dropOffDate || $[10] !== values.pickUpDate) {
    t2 = function handleClose() {
      batch(() => {
        formState$.pickUpDate.set(values.pickUpDate.get() as Date);
        formState$.dropOffDate.set(values.dropOffDate.get() as Date);
      });
    };
    $[7] = formState$.dropOffDate;
    $[8] = formState$.pickUpDate;
    $[9] = values.dropOffDate;
    $[10] = values.pickUpDate;
    $[11] = t2;
  } else {
    t2 = $[11];
  }
  const handleClose = t2;
  let t3;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      from: startOfMonth(new Date()),
      to: endOfMonth(addMonths(new Date(), 1))
    };
    $[12] = t3;
  } else {
    t3 = $[12];
  }
  const visibleRange$ = useObservable(t3);
  let t4;
  if ($[13] !== formState$ || $[14] !== selecting$) {
    t4 = () => {
      const selecting = selecting$.get();
      return selecting === "dropOff" ? formState$.getDropOffBranch() : formState$.getPickUpBranch();
    };
    $[13] = formState$;
    $[14] = selecting$;
    $[15] = t4;
  } else {
    t4 = $[15];
  }
  const activeBranch$ = useObservable(t4);
  let t5;
  if ($[16] !== activeBranch$ || $[17] !== visibleRange$.from || $[18] !== visibleRange$.to) {
    t5 = () => {
      const activeBranch = activeBranch$.get();
      const start = visibleRange$.from.get();
      const end = visibleRange$.to.get();
      const dates = [];
      const names = [];
      if (!activeBranch || !start || !end) {
        return {
          dates,
          names
        };
      }
      for (const date of eachDayOfInterval({
        start,
        end
      })) {
        const holiday = getBranchHolidaysForDate({
          branch: activeBranch,
          date
        });
        if (!holiday) {
          continue;
        }
        dates.push(date);
        if (!names.includes(holiday.name)) {
          names.push(holiday.name);
        }
      }
      return {
        dates,
        names
      };
    };
    $[16] = activeBranch$;
    $[17] = visibleRange$.from;
    $[18] = visibleRange$.to;
    $[19] = t5;
  } else {
    t5 = $[19];
  }
  const holidayDates$ = useObservable(t5);
  let t6;
  if ($[20] !== formState$) {
    t6 = () => {
      const searchPageUrl = formState$.isValid() ? formState$.getSearchUrl() : undefined;
      if (searchPageUrl) {
        const link = document.createElement("link");
        link.rel = "prefetch";
        link.href = searchPageUrl.href;
        document.head.appendChild(link);
        return () => {
          document.head.removeChild(link);
        };
      }
    };
    $[20] = formState$;
    $[21] = t6;
  } else {
    t6 = $[21];
  }
  useObserveEffect(t6);
  let t7;
  if ($[22] !== activeBranch$) {
    t7 = () => {
      const activeBranch_0 = activeBranch$.get();
      const now = new Date();
      const branchNow = activeBranch_0 ? toZonedTime(now, activeBranch_0.timeZone) : now;
      return addDays(branchNow, 1);
    };
    $[22] = activeBranch$;
    $[23] = t7;
  } else {
    t7 = $[23];
  }
  const minDate$ = useObservable(t7);
  let t8;
  if ($[24] !== variant$) {
    t8 = () => variant$.get() === "vertical";
    $[24] = variant$;
    $[25] = t8;
  } else {
    t8 = $[25];
  }
  let t9;
  if ($[26] !== triggerId$) {
    t9 = () => {
      triggerId$.set("locations");
    };
    $[26] = triggerId$;
    $[27] = t9;
  } else {
    t9 = $[27];
  }
  let t10;
  if ($[28] !== className) {
    t10 = cn("border-y-0 border-r border-l-0 flex-1 min-w-0 max-w-69 basis-69", className);
    $[28] = className;
    $[29] = t10;
  } else {
    t10 = $[29];
  }
  let t11;
  if ($[30] !== description || $[31] !== formState$.dropOffDate || $[32] !== formState$.pickUpDate) {
    t11 = () => {
      const pickUpDate = formState$.pickUpDate.get();
      const dropOffDate = formState$.dropOffDate.get();
      if (isValidDate(pickUpDate) && isValidDate(dropOffDate)) {
        return `${format(pickUpDate, "dd/MM/yy")} → ${format(dropOffDate, "dd/MM/yy")}`;
      }
      return description;
    };
    $[30] = description;
    $[31] = formState$.dropOffDate;
    $[32] = formState$.pickUpDate;
    $[33] = t11;
  } else {
    t11 = $[33];
  }
  let t12;
  if ($[34] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = {
      "data-testid": "dates-card",
      className: cn("[&_.card-body]:overflow-auto")
    };
    $[34] = t12;
  } else {
    t12 = $[34];
  }
  let t13;
  if ($[35] !== holidayDates$.names) {
    t13 = () => holidayDates$.names.get();
    $[35] = holidayDates$.names;
    $[36] = t13;
  } else {
    t13 = $[36];
  }
  let t14;
  if ($[37] !== activeBranch$ || $[38] !== selecting$) {
    t14 = () => {
      const prefix = selecting$.get() === "pickUp" ? "Pick up from " : "Drop off at ";
      const activeBranch_1 = activeBranch$.get();
      if (!activeBranch_1) {
        return "Select a location";
      }
      return <>{prefix} <strong>{activeBranch_1.name}</strong></>;
    };
    $[37] = activeBranch$;
    $[38] = selecting$;
    $[39] = t14;
  } else {
    t14 = $[39];
  }
  let t15;
  if ($[40] !== t13 || $[41] !== t14) {
    t15 = <HeroSearchFormDateRangeNote $holidays={t13} $title={t14} />;
    $[40] = t13;
    $[41] = t14;
    $[42] = t15;
  } else {
    t15 = $[42];
  }
  let t16;
  if ($[43] !== variant$) {
    t16 = () => variant$.get() === "vertical" ? 1 : 2;
    $[43] = variant$;
    $[44] = t16;
  } else {
    t16 = $[44];
  }
  let t17;
  if ($[45] !== visibleRange$) {
    t17 = date_0 => {
      visibleRange$.set({
        from: startOfMonth(date_0),
        to: endOfMonth(addMonths(date_0, 1))
      });
    };
    $[45] = visibleRange$;
    $[46] = t17;
  } else {
    t17 = $[46];
  }
  let t18;
  if ($[47] !== selecting$ || $[48] !== values.dropOffDate || $[49] !== values.pickUpDate) {
    t18 = (activeDate, activeModifier) => {
      const value = {
        from: values.pickUpDate.get(),
        to: values.dropOffDate.get()
      };
      const isDropOffDate = value?.to && isSameDay(activeDate, value.to);
      const isPickUpDate = value?.from && isSameDay(activeDate, value.from);
      if (activeModifier.range_middle || isDropOffDate && !isPickUpDate || value?.to && isAfter(activeDate, value.to)) {
        selecting$.set("dropOff");
        return;
      }
      selecting$.set("pickUp");
    };
    $[47] = selecting$;
    $[48] = values.dropOffDate;
    $[49] = values.pickUpDate;
    $[50] = t18;
  } else {
    t18 = $[50];
  }
  let t19;
  if ($[51] !== holidayDates$.dates) {
    t19 = () => holidayDates$.dates.get();
    $[51] = holidayDates$.dates;
    $[52] = t19;
  } else {
    t19 = $[52];
  }
  let t20;
  if ($[53] !== values.dropOffDate || $[54] !== values.pickUpDate) {
    t20 = () => ({
      from: values.pickUpDate.get(),
      to: values.dropOffDate.get()
    });
    $[53] = values.dropOffDate;
    $[54] = values.pickUpDate;
    $[55] = t20;
  } else {
    t20 = $[55];
  }
  let t21;
  if ($[56] !== minDate$) {
    t21 = () => minDate$.get();
    $[56] = minDate$;
    $[57] = t21;
  } else {
    t21 = $[57];
  }
  let t22;
  if ($[58] !== formState$.dropOffDate || $[59] !== formState$.pickUpDate || $[60] !== values.dropOffDate || $[61] !== values.pickUpDate) {
    t22 = value_0 => {
      batch(() => {
        if (!value_0) {
          values.pickUpDate.delete();
          values.dropOffDate.delete();
          return;
        }
        values.pickUpDate.set(value_0.from);
        values.dropOffDate.set(value_0.to);
        if (validateDateRange(value_0.from, value_0.to)) {
          formState$.pickUpDate.set(value_0.from);
          formState$.dropOffDate.set(value_0.to);
        }
      });
    };
    $[58] = formState$.dropOffDate;
    $[59] = formState$.pickUpDate;
    $[60] = values.dropOffDate;
    $[61] = values.pickUpDate;
    $[62] = t22;
  } else {
    t22 = $[62];
  }
  let t23;
  if ($[63] !== t16 || $[64] !== t17 || $[65] !== t18 || $[66] !== t19 || $[67] !== t20 || $[68] !== t21 || $[69] !== t22) {
    t23 = <ReactiveDateRangePicker $numberOfMonths={t16} onMonthChange={t17} onDayMouseEnter={t18} $closedDates={t19} $value={t20} $minDate={t21} onChange={t22} />;
    $[63] = t16;
    $[64] = t17;
    $[65] = t18;
    $[66] = t19;
    $[67] = t20;
    $[68] = t21;
    $[69] = t22;
    $[70] = t23;
  } else {
    t23 = $[70];
  }
  let t24;
  if ($[71] !== isNavigating$ || $[72] !== valid$) {
    t24 = () => !valid$.get() || isNavigating$.get();
    $[71] = isNavigating$;
    $[72] = valid$;
    $[73] = t24;
  } else {
    t24 = $[73];
  }
  let t25;
  if ($[74] !== formState$ || $[75] !== handleClose || $[76] !== isNavigating$ || $[77] !== open$ || $[78] !== triggerId$) {
    t25 = () => {
      handleClose();
      const searchPageUrl_0 = formState$.isValid() ? formState$.getSearchUrl() : undefined;
      if (searchPageUrl_0) {
        isNavigating$.set(true);
        window.location.href = searchPageUrl_0.href;
        formState$.trackBookingInitiated();
      } else {
        batch(() => {
          open$.set(false);
          triggerId$.set(null);
        });
      }
    };
    $[74] = formState$;
    $[75] = handleClose;
    $[76] = isNavigating$;
    $[77] = open$;
    $[78] = triggerId$;
    $[79] = t25;
  } else {
    t25 = $[79];
  }
  let t26;
  if ($[80] !== valid$) {
    t26 = () => valid$.get();
    $[80] = valid$;
    $[81] = t26;
  } else {
    t26 = $[81];
  }
  let t27;
  if ($[82] !== isNavigating$) {
    t27 = () => isNavigating$.get();
    $[82] = isNavigating$;
    $[83] = t27;
  } else {
    t27 = $[83];
  }
  let t28;
  if ($[84] !== t26 || $[85] !== t27) {
    t28 = <NextButtonLabel $isValid={t26} $isNavigating={t27} />;
    $[84] = t26;
    $[85] = t27;
    $[86] = t28;
  } else {
    t28 = $[86];
  }
  let t29;
  if ($[87] !== t24 || $[88] !== t25 || $[89] !== t28) {
    t29 = <$React.button className="btn btn-primary h-13 mt-auto" data-testid="date-continue-btn" $disabled={t24} onClick={t25}>{t28}</$React.button>;
    $[87] = t24;
    $[88] = t25;
    $[89] = t28;
    $[90] = t29;
  } else {
    t29 = $[90];
  }
  let t30;
  if ($[91] !== props || $[92] !== t10 || $[93] !== t11 || $[94] !== t15 || $[95] !== t23 || $[96] !== t29 || $[97] !== t8 || $[98] !== t9) {
    t30 = <ReactiveHeroSearchFormColumnButton $showBack={t8} onBack={t9} className={t10} $description={t11} data-testid="date-select" data-hero-field="dates" cardProps={t12} {...props}>{t15}{t23}{t29}</ReactiveHeroSearchFormColumnButton>;
    $[91] = props;
    $[92] = t10;
    $[93] = t11;
    $[94] = t15;
    $[95] = t23;
    $[96] = t29;
    $[97] = t8;
    $[98] = t9;
    $[99] = t30;
  } else {
    t30 = $[99];
  }
  return t30;
}
type HeroSearchFormDateRangeNoteProps = {
  title?: ReactNode;
  holidays: string[];
};
const HeroSearchFormDateRangeNote = reactive(({
  title,
  holidays
}: HeroSearchFormDateRangeNoteProps) => <FieldWarning>
        {title}
        {holidays.length ? <>
                :&nbsp;
                <ul className="inline-flex items-center gap-2 ml-2 list-disc">
                    {holidays?.map(holiday => <li key={holiday} className="flex items-center">
                            {holiday}
                        </li>)}
                </ul>
            </> : null}
    </FieldWarning>);
interface NextButtonLabelProps {
  isValid: boolean;
  isNavigating: boolean;
}
const NextButtonLabel = reactive(({
  isNavigating,
  isValid
}: NextButtonLabelProps) => {
  if (isNavigating) {
    return <>
                Please wait... <span className="loading loading-spinner loading-xs ml-2" />
            </>;
  }
  if (isValid) {
    return 'Search for vehicles';
  }
  return 'Select dates to continue';
});